import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Database, Settings, LogOut, Users, X, ArrowRightLeft, Check, ArrowDownUp, Upload, Download, CheckCircle, XCircle, Pause, Play, ChevronUp, ChevronDown, Search, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { activeDbSlug } from '../../services/activeDatabase';
import { useAuth } from '../../context/AuthContext';
import { useDatabaseSafe } from '../../context/DatabaseContext';
import { databasesApi, adminManagementApi } from '../../services/api';
import { isEntraAuth, graphPhotoScopes } from '../../auth/authConfig';
import { msalInstance } from '../../auth/authConfig';
import { useDataTransfer } from '../../context/DataTransferContext';
import { isDebugMode } from '../../utils/debug';
import { getRegenerateTarget, regenerateState } from '../../utils/exportRecipe';
import logo from '../../assets/logos/WM-Logo-CB-White-Header.png';
import useLogoMask from './useLogoMask';

const Header = ({ onNavigateHome, setCurrentPage }) => {
  const navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const { currentUser, isImpersonating, hasPersistedIdentity, users, isAdmin, isSysAdmin, access_Group, roleInDb, impersonate, stopImpersonating, claimIdentity, availableDatabases } = useAuth();
  const dbContext = useDatabaseSafe();
  const dbSlug = dbContext?.dbSlug || '';
  const orgLogoUrl = dbContext?.currentDb?.logoUrl || null;

  // Logos are masks painted in --color-bar-text, so they follow the theme's bar text color.
  // The org logo lives in blob storage, which sends no CORS headers; a mask image needs them,
  // so it is fetched through the API and masked from a local object URL.
  const [orgLogoSrc, setOrgLogoSrc] = useState(null);
  useEffect(() => {
    if (!orgLogoUrl || !dbSlug) { setOrgLogoSrc(null); return undefined; }
    let objectUrl = null;
    let live = true;
    databasesApi.getLogo(dbSlug)
      .then(res => {
        if (!live) return;
        objectUrl = URL.createObjectURL(res.data);
        setOrgLogoSrc(objectUrl);
      })
      .catch(() => { if (live) setOrgLogoSrc(null); });
    return () => {
      live = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [orgLogoUrl, dbSlug]);
  const wmLogoMask = useLogoMask(logo, 40);
  const orgLogoMask = useLogoMask(orgLogoSrc, 32, 160);
  const { activities, pauseActivity, restartActivity, reorderQueue, clearActivity, clearAllCompleted, clearMode, setClearMode } = useDataTransfer();

  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showImpersonateModal, setShowImpersonateModal] = useState(false);
  const [showDbSwitcherModal, setShowDbSwitcherModal] = useState(false);
  const [showActivityPanel, setShowActivityPanel] = useState(false);
  const [activitySearch, setActivitySearch] = useState('');
  const [impersonateSearch, setImpersonateSearch] = useState('');

  // Two-step impersonate flow (Josh 2026-08-06): step 1 = user picker,
  // step 2 = DB picker for the chosen user (shows which they have access
  // to, indicates current). Prevents landing on AccessDeniedPage when
  // the target user has no role in whatever DB SysAdmin was viewing.
  const [impersonateStep, setImpersonateStep] = useState(1);
  const [impersonateSelectedUser, setImpersonateSelectedUser] = useState(null);
  const [impersonateDbs, setImpersonateDbs] = useState([]);
  const [impersonateLoading, setImpersonateLoading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(null);

  // The DBs this dropdown can actually switch to. Menu visibility and the modal
  // body both read this one array, so they can never disagree — the server list
  // is the real user's, while roles belong to the effective (possibly
  // impersonated) one.
  const switchableDatabases = useMemo(() => {
    const dbs = availableDatabases || [];
    if (isSysAdmin) return dbs;
    return dbs.filter(db => {
      const slug = db.slug || (db.displayName || db.name)?.toLowerCase();
      return slug && currentUser?.roles?.[slug];
    });
  }, [availableDatabases, isSysAdmin, currentUser]);

  useEffect(() => {
    if (!isEntraAuth() || !msalInstance) return;
    const account = msalInstance.getAllAccounts()[0];
    if (!account) return;
    let objectUrl = null;
    const req = { ...graphPhotoScopes, account };

    const fetchPhoto = (token) =>
      fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(res => res.ok ? res.blob() : null)
      .then(blob => { if (blob) { objectUrl = URL.createObjectURL(blob); setAvatarUrl(objectUrl); } });

    msalInstance.acquireTokenSilent(req)
      .then(r => fetchPhoto(r.accessToken))
      .catch(() => {});

    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, []);

  const userDropdownRef = useRef(null);
  const impersonateRef = useRef(null);
  const dbSwitcherRef = useRef(null);
  const activityPanelRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
      if (impersonateRef.current && !impersonateRef.current.contains(event.target)) {
        setShowImpersonateModal(false);
      }
      if (dbSwitcherRef.current && !dbSwitcherRef.current.contains(event.target)) {
        setShowDbSwitcherModal(false);
      }
      if (activityPanelRef.current && !activityPanelRef.current.contains(event.target)) {
        setShowActivityPanel(false);
        setActivitySearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePreferences = () => {
    setShowUserDropdown(false);
    setCurrentPage('preferences');
  };

  const handleSignOut = () => {
    setShowUserDropdown(false);
    if (isEntraAuth() && msalInstance) {
      msalInstance.logoutRedirect();
    } else {
      console.log('Sign out clicked');
    }
  };

  const handleImpersonate = () => {
    setShowUserDropdown(false);
    setImpersonateSearch('');
    setImpersonateStep(1);
    setImpersonateSelectedUser(null);
    setImpersonateDbs([]);
    setShowImpersonateModal(true);
  };

  const closeImpersonateModal = () => {
    setShowImpersonateModal(false);
    setImpersonateSelectedUser(null);
    setImpersonateStep(1);
    setImpersonateDbs([]);
    setImpersonateSearch('');
  };

  // Step 1 → Step 2: resolve the target user's DB roles + fetch DB display
  // metadata (needed for the picker in step 2).
  const goToStep2 = async (user) => {
    setImpersonateSelectedUser(user);
    setImpersonateStep(2);
    setImpersonateLoading(true);
    try {
      // Get target user's roles (dict keyed by DB slug)
      const permRes = await adminManagementApi.resolveAllPermissions(user.id);
      const roles = permRes?.data?.roles || {};
      const targetSlugs = new Set(Object.keys(roles).map(s => s.toLowerCase()));

      // Fetch full DB list (SysAdmin sees all) so we have display metadata
      const dbsRes = await databasesApi.getAvailable();
      const allDbs = Array.isArray(dbsRes?.data) ? dbsRes.data : [];
      const userDbs = allDbs
        .filter(db => {
          const slug = (db.slug || db.displayName || '').toLowerCase();
          return targetSlugs.has(slug);
        })
        .map(db => ({
          ...db,
          role: roles[(db.slug || db.displayName || '').toLowerCase()]
        }));
      setImpersonateDbs(userDbs);
    } catch (err) {
      console.warn('Failed to load impersonate target DBs:', err);
      setImpersonateDbs([]);
    } finally {
      setImpersonateLoading(false);
    }
  };

  const goBackToStep1 = () => {
    setImpersonateStep(1);
    setImpersonateDbs([]);
  };

  const handleSwitchDatabase = () => {
    setShowUserDropdown(false);
    setShowDbSwitcherModal(true);
  };

  const handleSelectUser = (user) => {
    // Step 1 → 2 (defer the actual impersonate() until a DB is chosen).
    goToStep2(user);
  };

  const handleSelectImpersonateDb = (db) => {
    const slug = (db.slug || db.displayName || '').toLowerCase();

    // If target DB matches SysAdmin's CURRENT DB, just swap identity —
    // no navigation, no reload, no login-gate flash, no DB picker
    // (Josh 2026-08-06 UX).
    // If it's a different DB, soft-navigate via React Router (URL
    // change without full reload → also skips the login gate).
    const isSameDb = slug === dbSlug?.toLowerCase();

    // Pass empty targetSlug so AuthContext doesn't do its own
    // window.location.assign reload — we handle nav here.
    impersonate(impersonateSelectedUser, '');
    closeImpersonateModal();

    if (!isSameDb && slug) {
      navigate(`/${slug}/`);
    }
    // Notify listeners so DB-scoped contexts refetch as the new identity
    // (the same event DatabaseProvider fires on a database switch).
    window.dispatchEvent(new Event('pmd-database-changed'));
  };

  // Fallback for users with no DBs — impersonate without a target slug
  // and let maybeRedirectAfterImpersonation punt them to the picker.
  const handleImpersonateNoDb = () => {
    impersonate(impersonateSelectedUser);
    closeImpersonateModal();
  };

  const handleSelectDatabase = (db) => {
    const displayName = db.displayName || db.name;
    // URL slug from db.slug (the Slug column). Fallback to displayName.toLowerCase for safety.
    const slug = db.slug || displayName.toLowerCase();
    // Navigate to the new DB home. Only this tab moves: DatabaseProvider makes it this tab's
    // database once the slug resolves, and fires pmd-database-changed.
    navigate(`/${slug}/`);
    setShowDbSwitcherModal(false);
  };

  // Sort users: current user first, then alphabetically; filtered by search
  const sortedUsers = [...users]
    .sort((a, b) => {
      if (a.id === currentUser?.id) return -1;
      if (b.id === currentUser?.id) return 1;
      return (a.user_Name || '').localeCompare(b.user_Name || '');
    })
    .filter(u => !impersonateSearch || (u.user_Name || '').toLowerCase().includes(impersonateSearch.toLowerCase()));

  return (
    <header className="stui-header">
      <div className="stui-header-left">
        <span
          role="img"
          aria-label="WM Logo"
          className="stui-header-logo"
          style={wmLogoMask || undefined}
          onClick={onNavigateHome}
        />
        <span className="stui-header-title">DAWN</span>
      </div>
      {orgLogoMask && (
        <div className="stui-header-center">
          <span role="img" aria-label={dbContext?.currentDb?.organizationName || 'Organization logo'} className="stui-header-org-logo" style={orgLogoMask} />
        </div>
      )}
      <div className="stui-header-right">
        <div className="stui-header-user-info">
          {isImpersonating ? (
            <div className="stui-header-imp-area">
              <div className="stui-header-imp-icons">
                {/* Green check — "This is me" (claim identity, persists) */}
                {(!isEntraAuth()) && (
                  <button
                    className="stui-header-imp-btn stui-header-imp-claim"
                    onClick={() => claimIdentity()}
                    title="This is me — remember for next session"
                  >
                    <Check size={13} />
                  </button>
                )}
                {/* Red X — "Go back" (revert to identity) */}
                <button
                  className="stui-header-imp-btn stui-header-imp-revert"
                  onClick={stopImpersonating}
                  title="Stop impersonating"
                >
                  <X size={13} />
                </button>
              </div>
              <div className="stui-header-imp-text">
                <span className="stui-header-user-name stui-fw-semibold">
                  {currentUser?.user_Name || 'Unknown'} <span className="stui-header-imp-badge">Impersonating</span>
                </span>
                <span className="stui-header-user-group">
                  {dbSlug ? roleInDb(dbSlug) : access_Group}
                </span>
              </div>
            </div>
          ) : (
            <>
              <span className="stui-header-user-name">{currentUser?.user_Name || 'Loading...'}</span>
              <span className="stui-header-user-group">{dbSlug ? roleInDb(dbSlug) : access_Group}</span>
            </>
          )}
        </div>
        {/* ── Data Transfer Icon ── */}
        <div className="stui-activity-wrapper" ref={activityPanelRef}>
          <button
            className={`stui-activity-icon${activities.some(a => a.status === 'active') ? ' active' : ''}`}
            onClick={() => { setShowActivityPanel(p => !p); setActivitySearch(''); }}
            title="Data Transfer"
          >
            <ArrowDownUp size={20} />
            {activities.length > 0 && (
              <span className={`stui-activity-badge ${activities.some(a => a.status === 'active') ? 'stui-activity-badge-active' : 'stui-activity-badge-complete'}`}>
                {activities.length}
              </span>
            )}
          </button>

          {showActivityPanel && (
            <ActivityPanel
              activities={activities}
              search={activitySearch}
              setSearch={setActivitySearch}
              clearMode={clearMode}
              setClearMode={setClearMode}
              onPause={pauseActivity}
              onRestart={restartActivity}
              onReorder={reorderQueue}
              onDismiss={clearActivity}
              onClearAll={clearAllCompleted}
              onHistoryNav={() => {
                setShowActivityPanel(false);
                // SysAdmins land on the all-databases monitor. Everyone else opens the page under
                // the current database, where it offers the widest view their role there allows;
                // with no database chosen yet, choosing one comes first.
                if (isSysAdmin) { navigate('/admin/data-transfer-history'); return; }
                const slug = activeDbSlug();
                navigate(slug ? `/${slug}/data-transfer-history` : '/select-database');
              }}
            />
          )}
        </div>

        <div className="stui-header-user-menu" ref={userDropdownRef}>
          <button
            className="stui-header-user-icon"
            onClick={() => setShowUserDropdown(!showUserDropdown)}
          >
            {avatarUrl ? (
              <img src={avatarUrl} alt="" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', display: 'block' }} />
            ) : (
              <UserInitialsAvatar name={msalInstance?.getAllAccounts()[0]?.name || currentUser?.user_Name} />
            )}
          </button>
          {showUserDropdown && (
            <div className="stui-header-user-dropdown">
              {switchableDatabases.length > 1 && (
                <>
                  <button className="stui-header-user-option" onClick={handleSwitchDatabase}>
                    <ArrowRightLeft size={16} />
                    <span>Switch Database</span>
                  </button>
                  <div className="stui-header-dropdown-divider" />
                </>
              )}
              {(!isEntraAuth() || isSysAdmin) && (
                <>
                  <button className="stui-header-user-option" onClick={handleImpersonate}>
                    <Users size={16} />
                    <span>Impersonate User</span>
                  </button>
                  <div className="stui-header-dropdown-divider" />
                </>
              )}
              <button className="stui-header-user-option" onClick={handlePreferences}>
                <Settings size={16} />
                <span>Preferences</span>
              </button>
              <div className="stui-header-dropdown-divider" />
              <button className="stui-header-user-option" onClick={handleSignOut}>
                <LogOut size={16} />
                <span>Sign Out</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Impersonate Modal — two-step flow (user → database). */}
      {showImpersonateModal && (
        <div className="stui-modal-overlay">
          <div className="stui-modal stui-modal-md" ref={impersonateRef}>
            <div className="stui-modal-header">
              <h3>
                {impersonateStep === 1
                  ? 'Impersonate User'
                  : `Select Database — ${impersonateSelectedUser?.user_Name || ''}`}
              </h3>
              <button type="button" className="stui-modal-close" onClick={closeImpersonateModal}><X size={20} /></button>
            </div>

            <div className="stui-modal-body">
              {impersonateStep === 1 ? (
                <div className="detail-grid-24" style={{ marginBottom: 0 }}>
                  <div className="col-span-24">
                    <div className="detail-section-header" style={{ marginTop: 0 }}>Select a user</div>
                  </div>
                  <div className="form-group col-span-24">
                    <input
                      className="stui-field-input"
                      placeholder="Search users..."
                      value={impersonateSearch}
                      onChange={e => setImpersonateSearch(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <div className="col-span-24">
                    <div className="stui-modal-list">
                      {sortedUsers.map(user => (
                        <button
                          key={user.id}
                          className={`stui-modal-user ${user.id === currentUser?.id ? 'current' : ''}`}
                          onClick={() => handleSelectUser(user)}
                        >
                          <div className="stui-modal-user-info">
                            <span className="stui-modal-user-name">{user.user_Name}</span>
                            <span className="stui-modal-user-group">{user.organizationName || ''}</span>
                          </div>
                          {user.id === currentUser?.id && (
                            <span className="stui-modal-current-badge">Current</span>
                          )}
                        </button>
                      ))}
                      {sortedUsers.length === 0 && (
                        <div className="stui-label-note">No users match</div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="detail-grid-24" style={{ marginBottom: 0 }}>
                  <div className="col-span-24">
                    <div className="detail-section-header" style={{ marginTop: 0 }}>
                      Databases {impersonateSelectedUser?.user_Name ? `${impersonateSelectedUser.user_Name} ` : ''}can access
                    </div>
                  </div>
                  <div className="col-span-24">
                    {impersonateLoading ? (
                      <div className="stui-label-note">Loading databases...</div>
                    ) : impersonateDbs.length === 0 ? (
                      <div className="stui-label-note">
                        This user has no database access. You can still
                        impersonate them — they'll land on the database picker.
                      </div>
                    ) : (
                      <div className="stui-db-switcher-list">
                        {impersonateDbs.map(db => {
                          const name = db.displayName || db.name;
                          const slug = (db.slug || name || '').toLowerCase();
                          const isCurrent = slug === dbSlug?.toLowerCase();
                          return (
                            <button
                              key={db.id || slug}
                              className={`stui-db-switcher-row ${isCurrent ? 'current' : ''}`}
                              onClick={() => handleSelectImpersonateDb(db)}
                            >
                              <div className="stui-db-switcher-logo">
                                {db.iconUrl ? (
                                  <img src={db.iconUrl} alt="" />
                                ) : (
                                  <div className="stui-db-switcher-logo-placeholder">
                                    <Database size={16} />
                                  </div>
                                )}
                              </div>
                              <div className="stui-db-switcher-info">
                                <span className="stui-db-switcher-name">{name}</span>
                                {db.description && (
                                  <span className="stui-db-switcher-desc">{db.description}</span>
                                )}
                              </div>
                              {isCurrent && (
                                <span className="stui-db-switcher-badge">Current</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="stui-modal-footer">
              {impersonateStep === 2 && (
                <button className="stui-btn stui-btn-secondary" onClick={goBackToStep1}>
                  Back
                </button>
              )}
              <button className="stui-btn stui-btn-secondary" onClick={closeImpersonateModal}>
                Cancel
              </button>
              {impersonateStep === 2 && !impersonateLoading && impersonateDbs.length === 0 && (
                <button className="stui-btn stui-btn-primary" onClick={handleImpersonateNoDb}>
                  Impersonate Anyway
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Database Switcher Modal */}
      {showDbSwitcherModal && (
        <div className="stui-modal-overlay">
          <div className="stui-db-switcher-modal" ref={dbSwitcherRef}>
            <div className="stui-db-switcher-header">
              <div className="stui-flex-1 stui-min-w-0 stui-row-md">
                <img src={logo} alt="Wunderlich-Malec" className="stui-shrink-0" style={{ height: '32px' }} />
                <div style={{ borderLeft: '1px solid color-mix(in srgb, var(--color-bar-text) 30%, transparent)', paddingLeft: 'var(--spacing-md)' }}>
                  <div style={{ color: 'var(--color-bar-text)', fontWeight: 700, fontSize: 'var(--text-lg)', lineHeight: 1.1 }}>DAWN</div>
                  <div style={{ color: 'color-mix(in srgb, var(--color-bar-text) 65%, transparent)', fontSize: 'var(--text-xs)', letterSpacing: '0.04em', lineHeight: 1.2, marginTop: '2px' }}>
                    Documentation, Assets, Wiring &amp; Networking
                  </div>
                </div>
              </div>
              <button
                className="stui-modal-close"
                onClick={() => setShowDbSwitcherModal(false)}
              >
                &times;
              </button>
            </div>
            <div className="stui-db-switcher-list">
              {switchableDatabases.map(db => {
                const name = db.displayName || db.name;
                const dbSlugKey = db.slug || name?.toLowerCase();
                const isActive = dbSlugKey === dbSlug;
                return (
                  <button
                    key={db.id || db.databaseName}
                    className={`stui-db-switcher-row ${isActive ? 'current' : ''}`}
                    onClick={() => handleSelectDatabase(db)}
                  >
                    <div className="stui-db-switcher-logo">
                      {db.iconUrl ? (
                        <img src={db.iconUrl} alt="" />
                      ) : (
                        <div className="stui-db-switcher-logo-placeholder">
                          <Database size={16} />
                        </div>
                      )}
                    </div>
                    <div className="stui-db-switcher-info">
                      <span className="stui-db-switcher-name">{name}</span>
                      {db.description && (
                        <span className="stui-db-switcher-desc">{db.description}</span>
                      )}
                    </div>
                    {isActive && (
                      <span className="stui-db-switcher-badge">Current</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

// ── UserInitialsAvatar ────────────────────────────────────────────────────────
const DAWN_AVATAR_COLORS = [
  { bg: '#1a5568', text: '#ffffff' },
  { bg: '#2d7a9a', text: '#ffffff' },
  { bg: '#1e6b5a', text: '#ffffff' },
  { bg: '#3d5a80', text: '#ffffff' },
  { bg: '#4a5568', text: '#ffffff' },
  { bg: '#2c5f6e', text: '#ffffff' },
];

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const getAvatarColor = (name) => {
  if (!name) return DAWN_AVATAR_COLORS[0];
  const hash = [...name].reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return DAWN_AVATAR_COLORS[hash % DAWN_AVATAR_COLORS.length];
};

const UserInitialsAvatar = ({ name }) => {
  const { bg, text } = getAvatarColor(name);
  return (
    <div style={{
      width: 28, height: 28, borderRadius: '50%',
      background: bg, color: text,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 'var(--text-xs)', fontWeight: 700, fontFamily: 'var(--font-secondary)',
      letterSpacing: '0.5px', flexShrink: 0, userSelect: 'none'
    }}>
      {getInitials(name)}
    </div>
  );
};

// ── ActivityPanel ─────────────────────────────────────────────────────────────
const CLEAR_OPTIONS = [
  { label: '1m',    value: 'auto-1m' },
  { label: '1 day', value: 'auto-1d' },
  { label: '1 wk',  value: 'auto-1w' },
  { label: '1 mo',  value: 'auto-1mo' },
];

const FORMAT_COLORS = {
  pdf:    { bg: 'color-mix(in srgb, var(--color-error) 18%, var(--color-bg-white))', color: 'var(--color-error-text)' },
  excel:  { bg: 'color-mix(in srgb, var(--color-success) 18%, var(--color-bg-white))', color: 'var(--color-success-text)' },
  csv:    { bg: 'color-mix(in srgb, #2563eb 18%, var(--color-bg-white))', color: 'color-mix(in srgb, #2563eb 75%, var(--color-gray-900))' },
  import: { bg: 'color-mix(in srgb, var(--color-warning) 18%, var(--color-bg-white))', color: 'var(--color-warning-text)' },
};

const ActivityPanel = ({ activities, search, setSearch, clearMode, setClearMode, onPause, onRestart, onReorder, onDismiss, onClearAll, onHistoryNav }) => {
  const debug = isDebugMode();
  const q = search.toLowerCase();
  const filtered = q
    ? activities.filter(a => a.name.toLowerCase().includes(q) || a.format.toLowerCase().includes(q))
    : activities;

  const local    = filtered.filter(a => a.source === 'local');
  const otherTab = filtered.filter(a => a.source === 'other-tab');
  const remote   = filtered.filter(a => a.source === 'remote');

  // Group remote by machineName
  const remoteByMachine = remote.reduce((acc, a) => {
    const key = a.machineName || 'Unknown';
    if (!acc[key]) acc[key] = [];
    acc[key].push(a);
    return acc;
  }, {});

  const hasCompleted = activities.some(a => a.status === 'complete' || a.status === 'failed');

  return (
    <div className="stui-activity-panel">
      {/* Panel header */}
      <div className="stui-activity-panel-header">
        <div className="stui-row-8">
          <ArrowDownUp size={14} style={{ color: 'var(--color-primary-text)' }} />
          <span style={{ fontWeight: 700, fontSize: '12px', color: 'var(--color-gray-700)' }}>Data Transfer</span>
        </div>
        {hasCompleted && (
          <button className="stui-activity-clear-all-btn" onClick={onClearAll} title="Clear all completed">
            Clear Completed
          </button>
        )}
      </div>

      {/* Search */}
      <div className="stui-activity-search-row">
        <Search size={12} className="stui-c-muted stui-shrink-0" />
        <input
          className="stui-activity-search-input"
          placeholder="Filter transfers..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* Entry list */}
      <div className="stui-activity-entries-scroll">
        {local.length === 0 && otherTab.length === 0 && Object.keys(remoteByMachine).length === 0 && (
          <div className="stui-activity-empty">No active transfers</div>
        )}

        {local.map(a => (
          <ActivityEntry key={a.id} activity={a} debug={debug} isLocal
            onPause={() => onPause(a.id)}
            onRestart={() => onRestart(a.id)}
            onReorderUp={() => onReorder(a.id, 'up')}
            onReorderDown={() => onReorder(a.id, 'down')}
            onDismiss={clearMode === 'manual' ? () => onDismiss(a.id) : null}
          />
        ))}

        {otherTab.length > 0 && (
          <>
            <div className="stui-activity-source-header">This Machine</div>
            {otherTab.map(a => <ActivityEntry key={a.id} activity={a} debug={debug} />)}
          </>
        )}

        {Object.entries(remoteByMachine).map(([machine, entries]) => (
          <React.Fragment key={machine}>
            <div className="stui-activity-source-header">Remote · {machine}</div>
            {entries.map(a => <ActivityEntry key={a.id} activity={a} debug={debug} />)}
          </React.Fragment>
        ))}
      </div>

      {/* Footer */}
      <div className="stui-activity-panel-footer">
        <div className="stui-row-4" style={{ flexWrap: 'wrap' }}>
          <span className="stui-c-muted stui-fw-semibold stui-text-nowrap" style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Clear:</span>
          {CLEAR_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setClearMode(opt.value)}
              className={`stui-activity-clear-pill${clearMode === opt.value ? ' selected' : ''}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <button className="stui-activity-history-link" onClick={onHistoryNav}>
          <Clock size={11} /> History
        </button>
      </div>
    </div>
  );
};

const ActivityEntry = ({ activity: a, debug, isLocal, onPause, onRestart, onReorderUp, onReorderDown, onDismiss }) => {
  const navigate = useNavigate();
  const fmtColor = FORMAT_COLORS[a.format] || { bg: 'var(--color-gray-100)', color: 'var(--color-gray-600)' };

  // Re-download target, resolved up front so the button is only offered when it can actually work.
  const redownload = getRegenerateTarget(a.exportRecipe);
  const isActive  = a.status === 'active';
  const isPaused  = a.status === 'paused';
  const isQueued  = a.status === 'queued';
  const isDone    = a.status === 'complete';
  const isFailed  = a.status === 'failed';

  return (
    <div className={`stui-activity-entry${isDone ? ' done' : ''}${isFailed ? ' failed' : ''}`}>
      <div className="stui-entry-icon-col">
        {a.type === 'upload'
          ? <Upload size={13} style={{ color: 'var(--color-primary-text)' }} />
          : <Download size={13} style={{ color: 'var(--color-success-text)' }} />
        }
      </div>
      <div className="stui-entry-main-col">
        <div className="stui-entry-name-row">
          <span className="stui-entry-name">{a.name}</span>
          <span className="stui-entry-format-badge" style={{ background: fmtColor.bg, color: fmtColor.color }}>
            {a.format.toUpperCase()}
          </span>
          {debug && (
            <span className="stui-c-muted" style={{ fontSize: '9px', marginLeft: '4px' }}>
              {a.pct}% · {a.source}
            </span>
          )}
        </div>
        <div className="stui-entry-phase">{isQueued ? `Queued` : a.phase}</div>
        {(isActive || isPaused || isQueued) && (
          <div className="stui-entry-progress-track">
            <div className="stui-entry-progress-fill" style={{ width: `${Math.max(2, a.pct)}%`, background: isPaused ? 'var(--color-gray-300)' : isQueued ? 'var(--color-gray-200)' : 'var(--color-primary)' }} />
          </div>
        )}
      </div>
      <div className="stui-entry-actions-col">
        {isDone && <CheckCircle size={14} style={{ color: 'var(--color-success-text)' }} />}
        {isFailed && <XCircle size={14} className="stui-text-error" />}
        {isLocal && isActive && onPause && (
          <button className="stui-entry-action-btn" onClick={onPause} title="Pause"><Pause size={11} /></button>
        )}
        {isLocal && isPaused && onRestart && (
          <button className="stui-entry-action-btn" onClick={onRestart} title="Restart"><Play size={11} /></button>
        )}
        {isLocal && isQueued && (
          <>
            {onReorderUp && <button className="stui-entry-action-btn" onClick={onReorderUp} title="Move up"><ChevronUp size={11} /></button>}
            {onReorderDown && <button className="stui-entry-action-btn" onClick={onReorderDown} title="Move down"><ChevronDown size={11} /></button>}
          </>
        )}
        {(isDone || isFailed) && onDismiss && (
          <button className="stui-entry-action-btn" onClick={onDismiss} title="Dismiss"><X size={11} /></button>
        )}
        {isDone && redownload && !isLocal && (
          <button className="stui-entry-redownload-btn" title="Re-download" onClick={() => {
            // Router state, not a query string: the receiving effect reads location.state, which a
            // window.location.href assignment cannot carry.
            navigate(redownload.path, { state: regenerateState(redownload) });
          }}>↓</button>
        )}
      </div>
    </div>
  );
};

export default Header;
